import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { subscriptionsApi, type SubRow, type SubStatus } from '../features/subscriptions/api.ts';

const PILL: Record<SubStatus, string> = {
  ACTIVE: 'pill-success',
  GRACE: 'pill-pending',
  BLOCKED: 'pill-danger',
  NOT_SET: 'pill-neutral',
};
const LABEL: Record<SubStatus, string> = { ACTIVE: 'Active', GRACE: 'Grace', BLOCKED: 'Blocked', NOT_SET: 'Not set' };

function StatusBadge({ row }: { row: SubRow }): JSX.Element {
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <span className={`pill ${PILL[row.status]}`}>{LABEL[row.status]}</span>
      {row.daysLeft !== null && (
        <span className="text-xs text-muted-foreground tabular">
          {row.daysLeft >= 0 ? `${row.daysLeft}d left` : `${-row.daysLeft}d overdue`}
        </span>
      )}
    </span>
  );
}

function DateCell({ row }: { row: SubRow }): JSX.Element {
  const qc = useQueryClient();
  const [val, setVal] = useState(row.paidUntil?.slice(0, 10) ?? '');
  const save = useMutation({
    mutationFn: () => subscriptionsApi.setDate(row.id, val || null),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'subscriptions'] }),
  });
  return (
    <div className="flex items-center gap-2">
      <input
        type="date"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        className="input py-1.5 min-w-0 flex-1 md:flex-none md:w-auto"
      />
      <button
        type="button"
        className="btn-sm btn-secondary shrink-0"
        disabled={save.isPending}
        onClick={() => save.mutate()}
      >
        {save.isPending ? 'Saving…' : 'Save'}
      </button>
    </div>
  );
}

export function AdminSubscriptions(): JSX.Element {
  const qc = useQueryClient();
  const { data: rows } = useQuery({ queryKey: ['admin', 'subscriptions'], queryFn: subscriptionsApi.list });
  const { data: banner } = useQuery({ queryKey: ['admin', 'subscriptions', 'banner'], queryFn: subscriptionsApi.getBanner });
  const [text, setText] = useState<string | null>(null);
  const value = text ?? banner ?? '';
  const saveBanner = useMutation({
    mutationFn: () => subscriptionsApi.setBanner(value),
    onSuccess: () => { setText(null); qc.invalidateQueries({ queryKey: ['admin', 'subscriptions', 'banner'] }); },
  });

  const list = rows ?? [];

  return (
    <div className="page space-y-6">
      <header>
        <h1 className="text-[2rem] md:text-[2.5rem] font-normal leading-tight text-ink">Subscriptions</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manually track each council&apos;s paid-until date (5-day grace, then blocked).
        </p>
      </header>

      <section className="card-paper p-4 sm:p-5 space-y-3">
        <h2 className="text-sm font-medium text-muted-foreground">Warning banner</h2>
        <p className="text-sm text-muted-foreground">
          Shown to a council during grace/blocked.{' '}
          <code className="rounded bg-[hsl(var(--surface-container-high))] px-1.5 py-0.5 text-xs">
            {'{date}'}
          </code>{' '}
          is replaced by the renew-by date.
        </p>
        <textarea className="textarea" rows={3} value={value} onChange={(e) => setText(e.target.value)} />
        <p className="text-sm break-words">
          <span className="text-muted-foreground">Preview: </span>
          {value.replaceAll('{date}', '2026-06-30')}
        </p>
        <button
          type="button"
          className="btn-primary w-full sm:w-auto"
          disabled={saveBanner.isPending}
          onClick={() => saveBanner.mutate()}
        >
          {saveBanner.isPending ? 'Saving…' : 'Save banner'}
        </button>
      </section>

      {/* Desktop: table */}
      <section className="card-paper p-0 overflow-x-auto hidden md:block">
        <table className="w-full text-sm">
          <thead className="text-left">
            <tr className="border-b border-border/50">
              <th className="p-3 text-xs font-medium text-muted-foreground">Council</th>
              <th className="p-3 text-xs font-medium text-muted-foreground">Status</th>
              <th className="p-3 text-xs font-medium text-muted-foreground">Paid until</th>
              <th className="p-3 text-xs font-medium text-muted-foreground">Set / extend</th>
            </tr>
          </thead>
          <tbody>
            {list.map((row) => (
              <tr
                key={row.id}
                className="border-b border-border/40 transition-colors last:border-b-0 hover:bg-black/[0.03] dark:hover:bg-white/[0.04]"
              >
                <td className="p-3">
                  {row.name}
                  <div className="text-xs text-muted-foreground">{row.contactEmail}</div>
                </td>
                <td className="p-3"><StatusBadge row={row} /></td>
                <td className="p-3 whitespace-nowrap tabular">{row.paidUntil?.slice(0, 10) ?? '—'}</td>
                <td className="p-3"><DateCell row={row} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Mobile: stacked cards */}
      <div className="space-y-3 md:hidden">
        {list.map((row) => (
          <section key={row.id} className="card-paper p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium truncate">{row.name}</div>
                <div className="text-xs text-muted-foreground truncate">{row.contactEmail}</div>
              </div>
              <StatusBadge row={row} />
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Paid until</span>
              <span className="whitespace-nowrap tabular">{row.paidUntil?.slice(0, 10) ?? '—'}</span>
            </div>
            <DateCell row={row} />
          </section>
        ))}
      </div>

      {rows && list.length === 0 && (
        <p className="text-sm text-muted-foreground">No councils yet.</p>
      )}
    </div>
  );
}
